import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";


import { Column, usePagination, useSortBy, useTable } from 'react-table';

import TableCSVExport from '../../../component/TableCSVExport';

// Helper.
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize, getToolTipPosition } from "../../../helpers/utils";

// Types.
import Alert from "../../Alert";
import InstanceTarget from '../../../types/instance/InstanceTarget';
import Wait from "../../../types/instance/Wait";
import GroupedStatementsDataTable from '../../../types/instance/tables/GroupedStatementsDataTable';

// Components.
import GroupedStatementBreakdown from '../GroupedStatementBreakdown';
import ReactTable from '../../ReactTable';
import StatementToolTip from "./StatementToolTip";

// Constants.
import { DATA_LOADING, INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_POSTGRES } from "../../Constants";
import { INSTANCE_TYPE_SQLSERVER } from "../../Constants";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildRedirectQuery } from "../../../helpers/timeRangeUtils";
import SearchInput from '../../SearchInput';

function GroupedStatementsTable(props: {
    loading?: number,
    filtersParameters: string,
    instance: InstanceTarget,
    showByDefault: boolean,
    batchId?: string,
    instanceTime: number,
    waits: Wait[],
    statementsDataTable: GroupedStatementsDataTable[], currentTab?: string,
    tabIdPrefix?: string,
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void
}) {
    const tabIdPrefix = props.tabIdPrefix || '';
    const [visibleToolTip, setVisibleToolTip] = useState({
        id: '',
        position: 0
    });
    const timeRangeContext = useContext(TimeRangeContext)
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const getRedirectLink = (sqlHash: string) => {
        if (props.batchId !== undefined) {
            return `/instances/${props.instance.id}/activity/groupedbatch/${props.batchId}/groupedstatement/${sqlHash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`
        }
        return `/instances/${props.instance.id}/activity/groupedstatement/${sqlHash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`
    }

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    const data = useMemo(() => props.statementsDataTable.filter(row => {
        return (
            (row.grouphash === null ? '' : row.grouphash).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.sqltext === null ? '' : row.sqltext).toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.statement.sqlid === null ? '' : row.statement.sqlid).toLowerCase().includes(tableFilter.toLowerCase())
        )
    }), [props.statementsDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'statement',
            disableSortBy: true
        },
        {
            accessor: 'color',
            disableSortBy: true
        },
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            Header: '#',
            accessor: 'grouphash',
            className: 'concatenate-small col-width-10',
            defaultCanSort: true,
            sortType: (rowA, rowB, columnId) => {
                const a = Number(rowA.values[columnId]);
                const b = Number(rowB.values[columnId]);
                return a - b;
            },

            Cell: ({ cell }) => (
                <React.Fragment>
                    <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                    {props.batchId !== undefined ? (
                        <Link
                            to={`/instances/${props.instance.id}/activity/groupedbatch/${props.batchId}/groupedstatement/${cell.row.values.grouphash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`}>
                            {!tabIdPrefix && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" />} {cell.row.values.grouphash}
                        </Link>
                    ) : (
                        <Link
                            to={`/instances/${props.instance.id}/activity/groupedstatement/${cell.row.values.grouphash}/text?${buildRedirectQuery(timeRangeContext.timeRange)}`}>
                            {!tabIdPrefix && <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" />} {cell.row.values.grouphash}
                        </Link>
                    )}
                </React.Fragment>
            ),
        },
        {
            Header: 'Grouped Statement',
            accessor: 'sqltext',
            className: 'concatenate',
            disableSortBy: true,
            Cell: ({ cell }) => {
                if (!cell.row.values.sqltext) return <React.Fragment>-</React.Fragment>;

                // Inside panel: no icon, always link to page
                if (tabIdPrefix) {
                    return <Link to={getRedirectLink(cell.row.values.grouphash)}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
                }

                // Normal view: icon + click opens panel (if callback provided)
                if (props.onStatementClick) {
                    return (
                        <span
                            style={{ cursor: 'pointer', color: '#0d6efd' }}
                            onClick={(e) => {
                                e.preventDefault();
                                props.onStatementClick!(cell.row.values.grouphash, 'groupedstatement');
                            }}
                        >
                            <i className="fal fa-sidebar-flip fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in side panel" />
                            {cell.row.values.sqltext.substring(0, 200)}
                        </span>
                    );
                }

                return <Link to={getRedirectLink(cell.row.values.grouphash)}>{cell.row.values.sqltext.substring(0, 200)}</Link>;
            }
        },
        {
            Header: 'Breakdown',
            className: 'statement-waits col-width-10',
            disableSortBy: true,
            Cell: ({ cell }) => (
                cell.row.values.time !== null ? (
                    cell.row.values.id < 10 ? (
                        <GroupedStatementBreakdown filterParameters={props.filtersParameters}
                            statement={cell.row.values.statement} waits={props.waits}
                            instanceId={props.instance.id} instanceTime={props.instanceTime}
                            maxWaitTime={data[0]?.waittime}
                            batchId={props.batchId} />
                    ) : (
                        cell.row.values.id < 20 ? (
                            <GroupedStatementBreakdown filterParameters={props.filtersParameters}
                                statement={cell.row.values.statement} waits={props.waits}
                                instanceId={props.instance.id} instanceTime={props.instanceTime}
                                maxWaitTime={data[0]?.waittime}
                                batchId={props.batchId} />
                        ) : (
                            <React.Fragment>-</React.Fragment>
                        )
                    )
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            headerClassName: 'text-end',
            className: 'text-end col-width-10',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                cell.row.values.executions !== null && cell.row.values.executions !== 0 ? (
                    Helper.getFormattedNumber(cell.row.values.executions)
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Average Time',
            accessor: 'averagetime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => (
                cell.row.values.averagetime !== null && cell.row.values.executions !== null && cell.row.values.executions > 0 ? (
                    <React.Fragment>
                        {Helper.getTimeInEnglish(cell.row.values.averagetime)}
                    </React.Fragment>
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Total Time',
            accessor: 'waittime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5 text-nowrap',
            defaultCanSort: true,
            sortType: (rowA, rowB, columnId) => {
                const aWait = Number(rowA.values[columnId]) || 0;
                const bWait = Number(rowB.values[columnId]) || 0;

                if (aWait !== bWait) {
                    return bWait - aWait;
                }

                const aHash = Number(rowA.values['grouphash']) || 0;
                const bHash = Number(rowB.values['grouphash']) || 0;
                return aHash - bHash;
            },
            Cell: ({ cell }) => (
                cell.row.values.waittime !== null ? (
                    Helper.getTimeInEnglish(cell.row.values.waittime)
                ) : (
                    <React.Fragment>-</React.Fragment>
                )
            )
        },
        {
            Header: 'Weight %',
            accessor: 'weight',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }) => {
                const { waittime } = cell.row.values;
                const percentage = (100 / props.instanceTime) * waittime;

                return (
                    <React.Fragment>
                        {props.instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                    </React.Fragment>
                );
            }
        },
    ], [props.batchId, props.instance.id, props.instanceTime, props.filtersParameters, props.waits, props.instance.type]);

    let hiddenColumns: string[] = [];

    switch (props.instance.type) {
        case INSTANCE_TYPE_SQLSERVER:
        case INSTANCE_TYPE_ORACLE:

            hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];

            break;

        case INSTANCE_TYPE_POSTGRES:

            if (props.instance.use_pg_stat_statements === true) {

                // Show executions.
                hiddenColumns = ['statement', 'id', 'color'];
            } else {

                // Hide executions.
                hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];
            }

            break;

        default:

            hiddenColumns = ['statement', 'id', 'color', 'executions', 'averagetime'];

            break;
    }

    const tableInstance = useTable({
        columns,
        data,
        initialState: {
            hiddenColumns,
            pageIndex: initialPageIndex,
            pageSize: initialPageSize,
        }
    },
        useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = tableInstance;

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;

        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    return (
        <div id={`${tabIdPrefix}groupedstatements`}
            className={((props.showByDefault === true) ? 'tab-pane fade show active' : 'tab-pane fade')}
            role="tabpanel" aria-labelledby={`${tabIdPrefix}groupedstatements-tab`}>
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-scroll fa-fw" aria-hidden="true"></i>
                    Grouped Statements
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                        data-bs-target="#collapseStatements" aria-expanded="false"
                        aria-controls="collapseStatements"></i>
                </div>
                <div id="collapseStatements" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                        <Alert
                            message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                            heading="Statement records" variant="alert-info" />
                    )}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
                        <div className="col col-md-10">
                            <TableCSVExport
                                tableInstance={tableInstance}
                                filename="Statements"
                                instanceTime={props.instanceTime}
                            />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <ReactTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        canPreviousPage={canPreviousPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        pageCount={pageCount}
                        gotoPage={gotoPage}
                        nextPage={nextPage}
                        previousPage={previousPage}
                        setPageSize={setPageSize}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        dataLength={data.length}
                        error={null}
                        loading={props.loading === DATA_LOADING}
                        renderCell={(cell) => {
                            if (cell.column.id === "sqltext") {
                                return (
                                    <div className='tooltip-scroll ellipsis'
                                        onMouseEnter={(event) => setVisibleToolTip({
                                            id: cell.row.values.grouphash,
                                            position: getToolTipPosition(event)
                                        })}
                                        onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                    >
                                        {cell.render('Cell')}
                                        {cell.row.values.grouphash === visibleToolTip.id && (
                                            <StatementToolTip instanceType={props.instance.type}
                                                position={visibleToolTip.position}
                                                id={cell.row.values.grouphash}
                                                onClick={() => props.onStatementClick?.(cell.row.values.grouphash, 'groupedstatement')}
                                                cell={cell} />
                                        )}
                                    </div>
                                );
                            }
                            return cell.render('Cell');
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default GroupedStatementsTable;
